import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Users, MapPin, ChevronRight, Clock, ShieldCheck } from 'lucide-react';
import Header from '@components/layout/Header';
import Spinner from '@components/ui/Spinner';
import { useAuth } from '@context/AuthContext';
import { useTheme } from '@context/ThemeContext';
import {
  formatActivityDate,
  formatCountNoun,
  getCategoryInfo,
  isActivityListingEnded,
  isQuickLiveOngoing,
} from '@utils/helpers';
import api from '@utils/api';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'hosting', label: 'Hosting' },
  { id: 'past', label: 'Past' },
];

const CATEGORY_GRADIENTS = {
  sports: 'from-amber-400 via-orange-400 to-orange-500',
  fitness: 'from-rose-400 via-red-400 to-red-500',
  learning: 'from-emerald-400 via-teal-400 to-teal-500',
  social: 'from-violet-400 via-purple-400 to-purple-500',
  outdoors: 'from-lime-400 via-green-400 to-emerald-500',
  creative: 'from-pink-400 via-fuchsia-400 to-fuchsia-500',
  gaming: 'from-indigo-400 via-blue-500 to-blue-600',
  food: 'from-orange-400 via-amber-400 to-amber-500',
  music: 'from-rose-400 via-pink-400 to-pink-500',
  study: 'from-cyan-400 via-sky-400 to-blue-500',
  travel: 'from-sky-400 via-blue-400 to-indigo-500',
};

/** Hosting tab: 0 = in progress, 1 = future, 2 = ended (always last). Upcoming uses time-only sort. */
const getMyActivitiesListSortRank = (activity, now) => {
  if (isActivityListingEnded(activity)) return 2;
  const dt = activity.dateTime ? new Date(activity.dateTime).getTime() : NaN;
  if (Number.isNaN(dt)) return 2;
  if (dt <= now.getTime()) return 0;
  return 1;
};

const getStatusTag = (dateTime) => {
  const now = new Date();
  const target = new Date(dateTime);
  if (Number.isNaN(target.getTime())) return '';

  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const diffDays = Math.round((targetMidnight - nowMidnight) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return '';
};

/** Top-right diagonal ribbon on past activity cards (matches detail page red). */
const PastEndedCornerRibbon = ({ cancelled }) => (
  <div
    className="pointer-events-none absolute right-0 top-0 z-[21] h-[5.25rem] w-[5.25rem] overflow-hidden sm:h-[5.75rem] sm:w-[5.75rem]"
    aria-hidden
  >
    <div className="absolute right-[-2.35rem] top-[1.15rem] flex w-[9.25rem] rotate-45 items-center justify-center bg-[rgb(255_0_0/0.95)] py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white shadow-md sm:right-[-2.5rem] sm:top-5 sm:w-[10rem]">
      <span className="select-none">{cancelled ? 'Cancelled' : 'Ended'}</span>
    </div>
  </div>
);

const getCountdownLabel = (dateTime, nowMs) => {
  const targetMs = new Date(dateTime).getTime();
  if (Number.isNaN(targetMs)) return '';

  const diffMs = targetMs - nowMs;
  if (diffMs <= 0) return 'Ongoing';

  const minutes = Math.ceil(diffMs / 60000);
  const hours = Math.ceil(diffMs / 3600000);
  const days = Math.ceil(diffMs / 86400000);

  if (diffMs > 48 * 3600000) return `In ${days} day${days === 1 ? '' : 's'}`;
  if (diffMs >= 3600000) return `In ${hours} hour${hours === 1 ? '' : 's'}`;
  return `In ${minutes} min${minutes === 1 ? '' : 's'}`;
};

const ActivityDashCard = ({
  activity,
  role,
  showEndedRibbon = false,
  showCountdown = false,
  nowMs = Date.now(),
  onOpen,
}) => {
  const { dark } = useTheme();
  const endedGrey = showEndedRibbon;
  const participantCount = Array.isArray(activity.participants) ? activity.participants.length : 0;
  const statusTag = getStatusTag(activity.dateTime);
  const countdownLabel = showCountdown ? getCountdownLabel(activity.dateTime, nowMs) : '';
  const catInfo = getCategoryInfo(activity.category);
  const gradient = CATEGORY_GRADIENTS[activity.category] || CATEGORY_GRADIENTS.sports;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group block w-full overflow-hidden rounded-[20px] bg-white text-left shadow-[0_2px_16px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.03] transition-all duration-300 dark:bg-[#111827]/80 dark:ring-white/[0.06] dark:shadow-lg dark:shadow-black/20 ${
        endedGrey
          ? 'opacity-[0.58] saturate-[0.55] contrast-[0.92] hover:opacity-[0.72] hover:scale-[1.01] active:scale-[0.99] dark:hover:shadow-lg'
          : 'hover:scale-[1.01] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] active:scale-[0.98] dark:hover:shadow-xl dark:hover:shadow-brand-500/10'
      }`}
    >
      <div className="relative h-[200px] overflow-hidden select-none">
        {activity.image && String(activity.image).trim() !== '' ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{ backgroundImage: `url(${String(activity.image).trim()})` }}
              role="img"
              aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-black/10 to-black/45" />
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`}>
            <div className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-white/15 blur-2xl" />
            <div className="absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-black/10 blur-xl" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/40 via-black/15 to-transparent" />
        <div className={`absolute left-3 top-3 z-[5] flex max-w-[72%] flex-wrap gap-1.5 ${endedGrey ? 'opacity-80' : ''}`}>
          <span
            className={`rounded-full bg-black/25 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur-sm ${endedGrey ? 'opacity-90 grayscale-[0.35]' : ''}`}
          >
            {catInfo.emoji} {catInfo.label}
          </span>
          {activity.verifiedOnly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/75 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
              <ShieldCheck size={10} />
              Verified
            </span>
          )}
        </div>

        {endedGrey && (
          <div
            className={`pointer-events-none absolute inset-0 z-[4] ${dark ? 'bg-surface-950/45' : 'bg-white/35'}`}
            aria-hidden
          />
        )}

        {showEndedRibbon && (
          <PastEndedCornerRibbon cancelled={activity.status === 'cancelled'} />
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p
              className={`line-clamp-2 text-[15px] font-semibold leading-snug transition-colors duration-200 ${
                endedGrey
                  ? dark ? 'text-surface-400' : 'text-surface-500'
                  : 'text-surface-900 group-hover:text-brand-600 dark:text-white dark:group-hover:text-brand-400'
              }`}
            >
              {activity.title}
            </p>
          </div>
          <div className={`flex shrink-0 items-center gap-1.5 ${endedGrey ? 'opacity-75' : ''}`}>
            {countdownLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                <Clock size={10} />
                {countdownLabel}
              </span>
            ) : null}
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                role === 'Hosting'
                  ? 'bg-violet-50 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300'
                  : 'bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-300'
              }`}
            >
              {role}
            </span>
            {statusTag && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                {statusTag}
              </span>
            )}
            <ChevronRight
              size={16}
              className={endedGrey ? 'text-surface-400 dark:text-surface-500' : 'text-surface-300 dark:text-surface-600'}
            />
          </div>
        </div>

        <div className="mt-2.5 space-y-1.5">
          <div
            className={`flex items-center gap-2 text-[12.5px] text-surface-500 dark:text-gray-400 ${
              endedGrey ? '!text-surface-400 dark:!text-surface-500' : ''
            }`}
          >
            <Clock size={14} className="shrink-0 text-violet-400" />
            <span className="truncate">{formatActivityDate(activity.dateTime)}</span>
          </div>
          {activity.location?.address ? (
            <div
              className={`flex items-center gap-2 text-[12.5px] text-surface-500 dark:text-gray-400 ${
                endedGrey ? '!text-surface-400 dark:!text-surface-500' : ''
              }`}
            >
              <MapPin size={14} className="shrink-0 text-brand-400" />
              <span className="truncate">{activity.location.address}</span>
            </div>
          ) : null}
          <div
            className={`flex items-center gap-2 text-[12.5px] text-surface-500 dark:text-gray-400 ${
              endedGrey ? '!text-surface-400 dark:!text-surface-500' : ''
            }`}
          >
            <Users size={14} className="shrink-0 text-emerald-400" />
            <span>{formatCountNoun(participantCount, 'participant')}</span>
          </div>
        </div>

      </div>

    </button>
  );
};

const MyActivitiesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('upcoming');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!user?._id) return;
      try {
        const { data } = await api.get(`/users/${user._id}`);
        setActivities(data.activities || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?._id]);

  const buckets = useMemo(() => {
    const now = new Date();
    const ownId = String(user?._id || '');

    const isCreator = (a) => String(a.creator?._id || a.creator || '') === ownId;
    const isParticipant = (a) => (a.participants || []).some((p) => String(p?._id || p) === ownId);
    const inScope = (a) => isCreator(a) || isParticipant(a);

    const upcoming = activities
      .filter(
        (a) =>
          inScope(a) &&
          !isActivityListingEnded(a) &&
          (new Date(a.dateTime) > now || isQuickLiveOngoing(a, now))
      )
      .map((a) => ({ ...a, _role: isCreator(a) ? 'Hosting' : 'Joined' }))
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

    const hosting = activities
      .filter((a) => isCreator(a))
      .sort((a, b) => {
        const ra = getMyActivitiesListSortRank(a, now);
        const rb = getMyActivitiesListSortRank(b, now);
        if (ra !== rb) return ra - rb;
        return new Date(a.dateTime) - new Date(b.dateTime);
      })
      .map((a) => ({ ...a, _role: 'Hosting' }));

    const past = activities
      .filter(
        (a) =>
          inScope(a) &&
          new Date(a.dateTime) < now &&
          !isQuickLiveOngoing(a, now)
      )
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
      .map((a) => ({ ...a, _role: isCreator(a) ? 'Hosting' : 'Joined' }));

    return { upcoming, hosting, past };
  }, [activities, user?._id]);

  if (loading) return <Spinner className="min-h-screen" size="lg" />;

  const activeList = buckets[section];

  return (
    <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
      <Header />

      <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-5 sm:px-6 lg:px-8 lg:pb-10">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-500/15">
            <CalendarClock size={18} className="text-brand-500" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-surface-900 dark:text-white">My Vibes</h2>
            <p className="text-xs text-surface-500 dark:text-surface-400">Manage everything you are hosting or joined</p>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-1.5 rounded-2xl border border-surface-200/80 bg-white p-1 sm:gap-2 sm:p-1.5 dark:border-surface-700/60 dark:bg-surface-900/60">
          {SECTIONS.map((item) => {
            const count = buckets[item.id].length;
            const selected = section === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSection(item.id)}
                aria-label={`${item.label}, ${count} ${count === 1 ? 'vibe' : 'vibes'}`}
                aria-pressed={selected}
                className={`flex min-h-[44px] min-w-0 flex-1 items-center justify-center rounded-xl px-1.5 py-2 text-center transition-all active:scale-[0.98] sm:px-2.5 ${
                  selected ? 'gap-1.5 sm:gap-2' : 'gap-1 sm:gap-1.5'
                } ${
                  selected
                    ? 'bg-brand-500 text-white shadow-sm shadow-brand-500/25'
                    : 'text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800/80'
                }`}
              >
                <span className="min-w-0 truncate text-[11px] font-bold tracking-tight sm:text-xs">{item.label}</span>
                <span
                  className={`inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-extrabold tabular-nums leading-none sm:min-w-[1.4rem] sm:px-2 sm:text-[11px] ${
                    selected
                      ? 'bg-white text-brand-600 shadow-sm ring-1 ring-black/10 dark:text-brand-600 dark:ring-white/25'
                      : 'bg-surface-200/90 text-surface-600 ring-1 ring-surface-300/40 dark:bg-surface-700/90 dark:text-surface-200 dark:ring-surface-600/50'
                  }`}
                  aria-hidden
                >
                  {count > 99 ? '99+' : count}
                </span>
              </button>
            );
          })}
        </div>

        {activeList.length === 0 ? (
          <div className="card py-10 text-center">
            <p className="text-sm font-semibold text-surface-700 dark:text-surface-300">
              {section === 'upcoming'
                ? 'Nothing lined up. Change that.'
                : section === 'hosting'
                  ? "You're not hosting anything yet"
                  : 'No past vibes yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeList.map((activity) => {
              const showEndedRibbon =
                section === 'past' ||
                (section === 'hosting' && isActivityListingEnded(activity));
              return (
                <ActivityDashCard
                  key={activity._id}
                  activity={activity}
                  role={activity._role}
                  showEndedRibbon={showEndedRibbon}
                  showCountdown={section === 'upcoming'}
                  nowMs={nowMs}
                  onOpen={() => navigate(`/activity/${activity._id}`)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyActivitiesPage;
